// react
import { useContext, useMemo, useState } from 'react';

// hooks
import { useFetch } from '../../../../hooks/useFetch';

// types
import { Account, ActionButton, Pagination, Transaction } from '../../../../@types';

// router
import { useSearchParams } from 'react-router-dom';

// i18next
import { useTranslation } from 'react-i18next';

// context
import {
	ContextProject,
	ContextUserProjectPermissions,
} from '../../../pages/Project/Project.context';

// mui
import { Grid, Typography } from '@mui/material';

// ui
import { BtnExport } from '../../../ui/BtnExport/BtnExport';
import { BtnFilters } from '../../../ui/BtnFilters/BtnFilters';
import { BtnLegendTransaction } from '../../../ui/BtnLegendTransaction/BtnLegendTransaction';
import { EmptyProps } from '../../../ui/Empty/Empty';
import { FilterChangeEvent, FilterProps } from '../../../ui/Filters/Filters';
import { ListItemTextAccount } from '../../../ui/ListItemTextAccount/ListItemTextAccount';
import { TableHeaderProps } from '../../../ui/TableHeaders/TableHeader/TableHeader';
import { TableHeaders } from '../../../ui/TableHeaders/TableHeaders';
import { TableFooter } from '../../../ui/TableFooter/TableFooter';

// cards
import { CardEmpty } from '../../../cards/CardEmpty/CardEmpty';
import { CardTransfer } from '../../../cards/CardTransfer/CardTransfer';

// fields
import { SelectTransactionPage } from '../../../fields/SelectTransactionPage/SelectTransactionPage';

// forms
import { FormSearch } from '../../../forms/FormSearch/FormSearch';

// props
export interface TableTransfersProps {
	hasLegend?: boolean;
	hasSearch?: boolean;
	hasSelectPage?: boolean;
	isLoading?: boolean;
	pagination: Pagination;
	refetchTransactions?: () => void;
	transactions: Transaction[];
}

export const TableTransfers = ({
	hasLegend = true,
	hasSearch = true,
	hasSelectPage = true,
	isLoading,
	pagination,
	refetchTransactions,
	transactions,
}: TableTransfersProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// context
	const { project } = useContext(ContextProject);
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	// hooks
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramExpenseType = searchParams.get('expenseType');
	const paramFromAccount = searchParams.get('fromAccount');
	const paramQ = searchParams.get('q');
	const paramStatus = searchParams.get('status');
	const paramTags = searchParams.get('tags');
	const paramToAccount = searchParams.get('toAccount');

	// defaults
	const defaultFiltersValues = {
		fromAccount: 'all',
		status: 'all',
		tags: [],
		toAccount: 'all',
	};

	// state
	const [accounts, setAccounts] = useState<Account[]>([]);
	const [filtersValues, setFiltersValues] = useState({
		fromAccount: paramFromAccount || defaultFiltersValues.fromAccount,
		status: paramStatus || defaultFiltersValues.status,
		tags: paramTags ? paramTags.split(',') : defaultFiltersValues.tags,
		toAccount: paramToAccount || defaultFiltersValues.toAccount,
	});

	// fetch accounts
	useFetch({
		isEnabled: Boolean(project?.id),
		url: `${process.env.REACT_APP_API_URL}/accounts?project=${project?.id}&fields=currency,id,institution,name`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			setAccounts(res.data);
		},
	});

	// vars
	const headers: TableHeaderProps[] = [
		{
			GridProps: {
				lg: 1.5,
			},
			isSortable: true,
			label: t('date'),
			value: 'date',
		},
		{
			GridProps: {
				lg: 1.5,
			},
			label: t('from'),
			value: 'fromAccount',
		},
		{
			GridProps: {
				lg: 1.5,
			},
			label: t('sent'),
			value: 'amountSent',
		},
		{
			GridProps: {
				lg: 1.5,
			},
			label: t('status'),
			value: 'status',
		},
		{
			GridProps: {
				lg: 1.5,
			},
			label: t('to'),
			value: 'toAccount',
		},
		{
			GridProps: {
				lg: 1.5,
			},
			label: t('received'),
			value: 'amountReceived',
		},
		{
			GridProps: {
				lg: 1.5,
			},
			label: t('updated'),
			value: 'modified',
		},
	];

	const idFormFilters = 'form-transfers-filters';

	// options
	const optionsStatus = [
		{ label: t('failed'), value: 'FAILED' },
		{ label: t('reverted'), value: 'REVERTED' },
	];

	const optionsAccounts = accounts.map((account) => {
		return {
			label: <ListItemTextAccount account={account} />,
			secondary: account.institution,
			value: String(account.id),
		};
	});

	// handlers
	const handleChangeFilterValue = (e: FilterChangeEvent) => {
		const { name, value } = e.target as HTMLInputElement;
		setFiltersValues({
			...filtersValues,
			[name]: value,
		});
	};

	// filters
	const filters: FilterProps[] = [];

	if (userProjectPermissions?.account?.read) {
		filters.push(
			{
				hasAllOption: true,
				hasNoneOption: false,
				hasSearchParam: true,
				id: `${idFormFilters}-from-account`,
				label: t('fromAccount'),
				name: 'fromAccount',
				onChange: handleChangeFilterValue,
				options: optionsAccounts,
				type: 'SELECT',
				value: filtersValues.fromAccount,
			},
			{
				hasAllOption: true,
				hasNoneOption: false,
				hasSearchParam: true,
				id: `${idFormFilters}-to-account`,
				label: t('toAccount'),
				name: 'toAccount',
				onChange: handleChangeFilterValue,
				options: optionsAccounts,
				type: 'SELECT',
				value: filtersValues.toAccount,
			}
		);
	}

	filters.push({
		hasAllOption: true,
		hasNoneOption: false,
		hasSearchParam: true,
		id: `${idFormFilters}-status`,
		label: t('status'),
		name: 'status',
		onChange: handleChangeFilterValue,
		options: optionsStatus,
		type: 'SELECT',
		value: filtersValues.status,
	});

	if (userProjectPermissions?.tag?.read) {
		filters.push({
			hasSearchParam: true,
			id: `${idFormFilters}-tags`,
			label: t('tags'),
			multiple: true,
			name: 'tags',
			onChange: handleChangeFilterValue,
			type: 'TAGS',
			value: filtersValues.tags,
		});
	}

	// memos
	const empty = useMemo(() => {
		const emptyProps: EmptyProps = {
			actions: [],
			content: { children: t('empty.transfers.content') },
			IconCircleProps: {
				color: 'grey',
				type: 'transfer',
			},
			title: { children: t('empty.transfers.title') },
		};

		if (userProjectPermissions?.transfer?.create && emptyProps.actions) {
			emptyProps.actions.push({
				children: t('recordFundTransfer'),
				onClick: () => {
					searchParams.set('dialog', 'recordFundTransfer');
					setSearchParams(searchParams);
				},
				variant: 'contained' as ActionButton['variant'],
			});
		}

		if (paramQ || paramExpenseType) {
			emptyProps.actions = [];
			emptyProps.content = { children: t('empty.default.content') };
			emptyProps.IconCircleProps = { icon: null };
		}

		return emptyProps;
	}, [paramQ, paramExpenseType, searchParams, setSearchParams, t, userProjectPermissions]);

	const content = useMemo(() => {
		if (isLoading || !transactions.length) {
			return <CardEmpty empty={empty} isLoading={isLoading} />;
		}

		return (
			<Grid container spacing={2}>
				{transactions.map((transaction, i) => {
					return (
						<Grid item key={`transfer-${i}`} xs={12}>
							<CardTransfer refetchTransactions={refetchTransactions} transaction={transaction} />
						</Grid>
					);
				})}
			</Grid>
		);
	}, [empty, isLoading, refetchTransactions, transactions]);

	return (
		<Grid container spacing={2}>
			<Grid item xs={12}>
				<Grid container spacing={2}>
					{hasSelectPage && (
						<Grid item xs={12} md={3} lg={2}>
							<SelectTransactionPage value="transfers" />
						</Grid>
					)}
					{hasSearch && (
						<Grid item xs={12} md={true}>
							<FormSearch InputProps={{ placeholder: t('searchByIdOrEntity') }} />
						</Grid>
					)}
					{hasLegend && (
						<Grid item xs={4} md="auto">
							<BtnLegendTransaction />
						</Grid>
					)}
					<Grid item xs={hasLegend ? 4 : 6} md="auto">
						<BtnExport resource="TRANSFER" />
					</Grid>
					<Grid item xs={hasLegend ? 4 : 6} md="auto">
						<BtnFilters
							filters={filters}
							onReset={() => {
								setFiltersValues(defaultFiltersValues);
							}}
						/>
					</Grid>
				</Grid>
			</Grid>
			<Grid item xs={12} sx={{ display: { xs: 'none', lg: 'block' } }}>
				<TableHeaders headers={headers} />
			</Grid>
			<Grid item xs={12}>
				{content}
			</Grid>
			<Grid item xs={12}>
				<TableFooter
					footer={
						<Typography color="brand.grey.500" variant="caption">
							<Typography component="span" color="error">
								*
							</Typography>{' '}
							{t('tableTransfers.footer')}
						</Typography>
					}
					numShowing={transactions.length}
					pagination={pagination}
				/>
			</Grid>
		</Grid>
	);
};
